import { useAuth0 } from "@auth0/auth0-react";
import {
  BeakerIcon,
  BellIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  CommandLineIcon,
  DocumentTextIcon,
  ScaleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { setRumUser } from "@utils/rum";
import { BasecampRole } from "@utils/types";
import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";

import { NavItem } from "@fronterahealth/frontera-ui-components";

import { setRestAuthGetter, setRestBaseUrl } from "@api/rest/rest-service";
import { useBasecampRoles } from "@hooks/useBasecampRoles";

import { DesktopTopBar } from "./DesktopTopBar";

export const AppShell: React.FC = () => {
  const { user, logout, getAccessTokenSilently } = useAuth0();
  const roles = useBasecampRoles();
  const { pathname } = useLocation();

  const isAdmin = roles.includes(BasecampRole.ADMIN);
  const showEnablement = isAdmin || roles.includes(BasecampRole.ENABLEMENT_ADMIN);
  const showEnterprise = isAdmin || roles.includes(BasecampRole.ENTERPRISE_ADMIN);
  const showNotifications = isAdmin || roles.includes(BasecampRole.NOTIFICATIONS_ADMIN);
  const showExpertChats = isAdmin || roles.includes(BasecampRole.CHAT_ADMIN);

  const onNotifications = pathname.startsWith("/notifications");
  // User can manually expand from other routes; always open when on /notifications
  const [manuallyOpened, setManuallyOpened] = useState(false);
  const notificationsOpen = onNotifications || manuallyOpened;

  // Same expand pattern as Notifications: always open on any evaluations
  // route, and manually toggleable from elsewhere.
  const onEvaluations =
    pathname.startsWith("/evaluations") ||
    pathname.startsWith("/eval-results") ||
    pathname.startsWith("/eval-rubrics") ||
    pathname.startsWith("/model-testing");
  const [evaluationsManuallyOpened, setEvaluationsManuallyOpened] = useState(false);
  const evaluationsOpen = onEvaluations || evaluationsManuallyOpened;

  useEffect(() => {
    setRestBaseUrl(import.meta.env.VITE_FRONTERA_REST_ENDPOINT ?? "");
    setRestAuthGetter(async () => {
      const token = await getAccessTokenSilently();
      const headers = new Headers();
      headers.append("Authorization", `Bearer ${token}`);
      return headers;
    });
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (user?.sub) {
      setRumUser(user.sub);
    }
  }, [user?.sub]);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } }).catch((e: unknown) => {
      console.error("Logout failed", e);
    });
  };

  const sidePanelContent = (
    <ul className="list-none p-0 m-0">
      {(showEnablement || showEnterprise) && (
        <NavLink to="/organizations" end={false}>
          {({ isActive }) => (
            <NavItem name="Organizations" href="/organizations" Icon={BuildingOffice2Icon} active={isActive} />
          )}
        </NavLink>
      )}
      {showEnablement && (
        <NavLink to="/verification-requests" end={false}>
          {({ isActive }) => (
            <NavItem
              name="Verification Requests"
              href="/verification-requests"
              Icon={ClipboardDocumentCheckIcon}
              active={isActive}
            />
          )}
        </NavLink>
      )}
      {showExpertChats && (
        <NavLink to="/expert-chats" end={false}>
          {({ isActive }) => (
            <NavItem name="Expert Chats" href="/expert-chats" Icon={ChatBubbleLeftRightIcon} active={isActive} />
          )}
        </NavLink>
      )}
      {showEnterprise && (
        <NavLink to="/prompts" end={false}>
          {({ isActive }) => <NavItem name="Prompts" href="/prompts" Icon={CommandLineIcon} active={isActive} />}
        </NavLink>
      )}
      {showEnterprise && (
        <NavLink to="/templates" end={false}>
          {({ isActive }) => <NavItem name="Templates" href="/templates" Icon={DocumentTextIcon} active={isActive} />}
        </NavLink>
      )}
      {showEnterprise && (
        <>
          <NavItem
            name="Evaluations"
            href=""
            Icon={BeakerIcon}
            active={onEvaluations}
            hasChildren
            isOpen={evaluationsOpen}
            onItemClick={() => setEvaluationsManuallyOpened((v) => !v)}
          />
          {evaluationsOpen && (
            <div className="relative ml-4 space-y-1">
              <div className="absolute left-0 -top-4 h-full border-l border-border-subtle" />
              <div className="flex flex-col pl-4 gap-2">
                <Link to="/evaluations/profiles">
                  <NavItem iconOnly={false} name="Profiles" href="#" active={pathname.startsWith("/evaluations")} />
                </Link>
                <Link to="/eval-results">
                  <NavItem iconOnly={false} name="Results" href="#" active={pathname.startsWith("/eval-results")} />
                </Link>
                <Link to="/eval-rubrics">
                  <NavItem iconOnly={false} name="Rubric" href="#" active={pathname.startsWith("/eval-rubrics")} />
                </Link>
                <Link to="/model-testing">
                  <NavItem
                    iconOnly={false}
                    name="Model Testing"
                    href="#"
                    active={pathname.startsWith("/model-testing")}
                  />
                </Link>
              </div>
            </div>
          )}
        </>
      )}
      {showEnterprise && (
        <NavLink to="/base-criteria" end={false}>
          {({ isActive }) => (
            <NavItem name="Base QA Criteria" href="/base-criteria" Icon={ShieldCheckIcon} active={isActive} />
          )}
        </NavLink>
      )}
      {(showEnablement || showEnterprise) && (
        <NavLink to="/export-testing" end={false}>
          {({ isActive }) => (
            <NavItem name="Export Testing" href="/export-testing" Icon={BeakerIcon} active={isActive} />
          )}
        </NavLink>
      )}
      {showEnterprise && (
        <NavLink to="/qa-rules" end={false}>
          {({ isActive }) => <NavItem name="QA Rules" href="/qa-rules" Icon={ScaleIcon} active={isActive} />}
        </NavLink>
      )}
      {showNotifications && (
        <>
          <NavItem
            name="Notifications"
            href=""
            Icon={BellIcon}
            active={onNotifications}
            hasChildren
            isOpen={notificationsOpen}
            onItemClick={() => setManuallyOpened((v) => !v)}
          />
          {notificationsOpen && (
            <div className="relative ml-4 space-y-1">
              <div className="absolute left-0 -top-4 h-full border-l border-border-subtle" />
              <div className="flex flex-col pl-4 gap-2">
                <Link to="/notifications/community-digest">
                  <NavItem
                    iconOnly={false}
                    name="Community Digest"
                    href="#"
                    active={pathname.startsWith("/notifications/community-digest")}
                  />
                </Link>
              </div>
            </div>
          )}
        </>
      )}
    </ul>
  );

  return (
    <div className="flex flex-col">
      <DesktopTopBar
        user={{
          imageSrc: user?.picture ?? "",
          title: user?.name ?? "",
          logout: handleLogout,
          orgName: user?.email ?? "",
        }}
        sidePanelContent={sidePanelContent}
      />
      <main className="flex-1 bg-utility-background-default">
        <Outlet />
      </main>
    </div>
  );
};
